import { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Box,
  Plus,
  Trash2,
  Edit,
  AlertCircle,
  AlertTriangle,
  Eye,
  Database
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { collection, query, getDocs, addDoc, doc, updateDoc, orderBy, serverTimestamp } from 'firebase/firestore'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { OrganizationContext } from './AssistPage'

interface SignalModelField {
  name: string
  type: string
  required?: boolean
}

interface SignalModel {
  id: string
  slug?: string
  title?: string
  name?: string  // Admin API uses 'name' instead of 'title'
  category?: string  // Admin API uses 'category' instead of 'slug'
  description?: string
  requiredFields?: string[]  // UI format: array of field names
  fields?: SignalModelField[]  // Admin API format: array of field objects
  organizationId?: string
  status?: 'configured' | 'discovered'
  isActive?: boolean  // Admin API format
  indexingEnabled?: boolean  // Whether this model is indexed for signals (default: false)
  deletedAt?: Date | null  // Soft delete timestamp
  createdAt: Date
  updatedAt: Date
}

interface ModelField {
  name: string
  type: string
}

// Helper to get display name (handles both 'title' and 'name')
function getModelDisplayName(model: SignalModel): string {
  return model.title || model.name || 'Untitled'
}

// Helper to get model fields in a consistent format
function getModelFields(model: SignalModel): { name: string; type: string }[] {
  // If we have the new 'fields' format (from Admin API)
  if (model.fields && Array.isArray(model.fields)) {
    return model.fields.map(f => ({ name: f.name, type: f.type }))
  }
  // Fall back to old 'requiredFields' format (from UI)
  if (model.requiredFields && Array.isArray(model.requiredFields)) {
    return model.requiredFields.map(name => ({ name, type: 'string' }))
  }
  return []
}

const FIELD_TYPES = ['string', 'number', 'boolean', 'date', 'array', 'object']

export function SignalsModelsPage() {
  const orgContext = useOutletContext<OrganizationContext>()
  const [models, setModels] = useState<SignalModel[]>([])
  const [loading, setLoading] = useState(true)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingModel, setEditingModel] = useState<SignalModel | null>(null)
  const [viewingModel, setViewingModel] = useState<SignalModel | null>(null)
  const [deleteConfirmModel, setDeleteConfirmModel] = useState<SignalModel | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  // Form state
  const [modelName, setModelName] = useState('')
  const [modelDescription, setModelDescription] = useState('')
  const [fields, setFields] = useState<ModelField[]>([{ name: '', type: 'string' }])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchModels = async () => {
      const orgDocId = orgContext?.organizationId
      if (!orgDocId) {
        setLoading(false)
        return
      }
      
      try {
        const db = getFirebaseDb()
        setOrganizationId(orgDocId)
        
        // Fetch signal models from organization subcollection
        const modelsRef = collection(db, 'organizations', orgDocId, 'signalModels')
        const modelsQuery = query(modelsRef, orderBy('createdAt', 'desc'))
        const modelsSnapshot = await getDocs(modelsQuery)

        // Filter out soft-deleted models client-side to avoid composite index requirement
        const modelsData = modelsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
            deletedAt: doc.data().deletedAt?.toDate() || null
          }))
          .filter(model => !model.deletedAt) as SignalModel[]
        
        setModels(modelsData)
      } catch (error) {
        console.error('Error fetching models:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchModels()
  }, [orgContext?.organizationId])

  const resetForm = () => {
    setModelName('')
    setModelDescription('')
    setFields([{ name: '', type: 'string' }])
    setError(null)
    setEditingModel(null)
  }

  const addField = () => {
    setFields([...fields, { name: '', type: 'string' }])
  }

  const removeField = (index: number) => {
    if (fields.length > 1) {
      setFields(fields.filter((_, i) => i !== index))
    }
  }

  const updateField = (index: number, key: keyof ModelField, value: string) => {
    const newFields = [...fields]
    newFields[index] = { ...newFields[index], [key]: value }
    setFields(newFields)
  }

  const handleCreate = async () => {
    if (!organizationId || !modelName.trim()) {
      setError('Model name is required')
      return
    }

    const validFields = fields.filter(f => f.name.trim())
    if (validFields.length === 0) {
      setError('At least one field is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const db = getFirebaseDb()
      const modelsRef = collection(db, 'organizations', organizationId, 'signalModels')
      
      const slug = modelName.trim().toLowerCase().replace(/\s+/g, '-')
      const newModel = await addDoc(modelsRef, {
        slug,
        title: modelName.trim(),
        description: modelDescription.trim() || null,
        requiredFields: validFields.map(f => f.name),
        status: 'configured',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      const now = new Date()
      setModels([{
        id: newModel.id,
        slug: modelName.trim().toLowerCase().replace(/\s+/g, '-'),
        title: modelName.trim(),
        description: modelDescription.trim() || undefined,
        requiredFields: validFields.map(f => f.name),
        organizationId,
        status: 'configured',
        createdAt: now,
        updatedAt: now
      }, ...models])

      resetForm()
      setIsCreateOpen(false)
    } catch (error) {
      console.error('Error creating model:', error)
      setError('Failed to create model')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async () => {
    if (!organizationId || !editingModel || !modelName.trim()) {
      setError('Model name is required')
      return
    }

    const validFields = fields.filter(f => f.name.trim())
    if (validFields.length === 0) {
      setError('At least one field is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const db = getFirebaseDb()
      const modelRef = doc(db, 'organizations', organizationId, 'signalModels', editingModel.id)
      
      await updateDoc(modelRef, {
        title: modelName.trim(),
        description: modelDescription.trim() || null,
        requiredFields: validFields.map(f => f.name),
        updatedAt: serverTimestamp()
      })

      setModels(models.map(m =>
        m.id === editingModel.id
          ? { ...m, title: modelName.trim(), description: modelDescription.trim() || undefined, requiredFields: validFields.map(f => f.name), updatedAt: new Date() }
          : m
      ))

      resetForm()
      setIsCreateOpen(false)
    } catch (error) {
      console.error('Error updating model:', error)
      setError('Failed to update model')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (model: SignalModel) => {
    if (!organizationId) return

    try {
      const db = getFirebaseDb()
      const modelRef = doc(db, 'organizations', organizationId, 'signalModels', model.id)

      // Soft delete - set deletedAt timestamp
      await updateDoc(modelRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      setModels(models.filter(m => m.id !== model.id))
      setDeleteConfirmModel(null)
      setDeleteConfirmText('')
      setViewingModel(null)
    } catch (error) {
      console.error('Error deleting model:', error)
    }
  }

  const handleToggleIndexing = async (model: SignalModel, enabled: boolean) => {
    if (!organizationId) return

    try {
      const db = getFirebaseDb()
      const modelRef = doc(db, 'organizations', organizationId, 'signalModels', model.id)

      await updateDoc(modelRef, {
        indexingEnabled: enabled,
        updatedAt: serverTimestamp()
      })

      // Update local state
      setModels(models.map(m =>
        m.id === model.id
          ? { ...m, indexingEnabled: enabled, updatedAt: new Date() }
          : m
      ))

      // Also update viewingModel if it's the same model
      if (viewingModel?.id === model.id) {
        setViewingModel({ ...viewingModel, indexingEnabled: enabled, updatedAt: new Date() })
      }
    } catch (error) {
      console.error('Error toggling indexing:', error)
    }
  }

  const startEditing = (model: SignalModel) => {
    setEditingModel(model)
    setModelName(getModelDisplayName(model))
    setModelDescription(model.description || '')
    const modelFields = getModelFields(model)
    setFields(modelFields.length > 0 ? modelFields : [{ name: '', type: 'string' }])
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Box className="h-8 w-8" />
          <div>
            <h1 className="text-3xl font-bold">Signal Models</h1>
            <p className="text-muted-foreground">
              Define data structures for your tracked events
            </p>
          </div>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={(open) => {
          setIsCreateOpen(open)
          if (!open) resetForm()
        }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Create Model
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingModel ? 'Edit Signal Model' : 'Create Signal Model'}</DialogTitle>
              <DialogDescription>
                {editingModel ? 'Update the model configuration' : 'Define a new data structure for tracking events'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {error && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name">Model Name</Label>
                <Input
                  id="name"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  placeholder="e.g., PageView, Purchase, UserAction"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  value={modelDescription}
                  onChange={(e) => setModelDescription(e.target.value)}
                  placeholder="What does this model track?"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fields</Label>
                <div className="space-y-2">
                  {fields.map((field, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={field.name}
                        onChange={(e) => updateField(index, 'name', e.target.value)}
                        placeholder="Field name"
                        className="flex-1"
                      />
                      <select
                        value={field.type}
                        onChange={(e) => updateField(index, 'type', e.target.value)}
                        className="w-32 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {FIELD_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeField(index)}
                        disabled={fields.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addField}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={editingModel ? handleUpdate : handleCreate} disabled={saving}>
                {saving ? (editingModel ? 'Saving...' : 'Creating...') : (editingModel ? 'Save Changes' : 'Create Model')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Models Grid */}
      {models.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Box className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Signal Models</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Create your first model to define the structure of your tracked events
              </p>
              <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Model
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {models.map((model) => (
            <Card
              key={model.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => setViewingModel(model)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{getModelDisplayName(model)}</CardTitle>
                    {model.category && (
                      <Badge variant="secondary" className="text-xs">
                        {model.category}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation()
                      startEditing(model)
                      setIsCreateOpen(true)
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
                {model.description && (
                  <CardDescription className="line-clamp-2">{model.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{getModelFields(model).length} fields</span>
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span>View details</span>
                    </div>
                  </div>
                  <div
                    className="flex items-center justify-between pt-2 border-t"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <Database className="h-4 w-4 text-muted-foreground" />
                      <span className={model.indexingEnabled ? 'text-foreground' : 'text-muted-foreground'}>
                        Indexing
                      </span>
                    </div>
                    <Switch
                      checked={model.indexingEnabled ?? false}
                      onCheckedChange={(checked) => handleToggleIndexing(model, checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Model Modal */}
      <Dialog open={!!viewingModel} onOpenChange={(open) => !open && setViewingModel(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          {viewingModel && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{getModelDisplayName(viewingModel)}</DialogTitle>
                    {viewingModel.category && (
                      <Badge variant="secondary" className="mt-2">
                        {viewingModel.category}
                      </Badge>
                    )}
                  </div>
                </div>
                {viewingModel.description && (
                  <DialogDescription className="text-left">
                    {viewingModel.description}
                  </DialogDescription>
                )}
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div>
                  <h4 className="text-sm font-semibold mb-3">Fields ({getModelFields(viewingModel).length})</h4>
                  <div className="space-y-2">
                    {getModelFields(viewingModel).map((field, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded-lg border px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <code className="text-sm font-semibold">{field.name}</code>
                        </div>
                        <Badge variant="outline">{field.type}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between py-3 border-t">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium">Indexing</div>
                      <div className="text-xs text-muted-foreground">
                        {viewingModel.indexingEnabled
                          ? 'Events with this model will be indexed'
                          : 'Events with this model are not indexed'}
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={viewingModel.indexingEnabled ?? false}
                    onCheckedChange={(checked) => handleToggleIndexing(viewingModel, checked)}
                  />
                </div>

                <div className="text-xs text-muted-foreground pt-2 border-t">
                  <div className="flex justify-between">
                    <span>Created: {viewingModel.createdAt.toLocaleDateString()}</span>
                    <span>Updated: {viewingModel.updatedAt.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteConfirmModel(viewingModel)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Model
                </Button>
                <div className="flex-1" />
                <Button
                  variant="outline"
                  onClick={() => {
                    startEditing(viewingModel)
                    setViewingModel(null)
                    setIsCreateOpen(true)
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={() => setViewingModel(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmModel} onOpenChange={(open) => {
        if (!open) {
          setDeleteConfirmModel(null)
          setDeleteConfirmText('')
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Signal Model
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{deleteConfirmModel && getModelDisplayName(deleteConfirmModel)}</strong>?
              </p>
              <p className="text-sm">
                This action will archive the model. Existing data will be preserved but the model will no longer be active.
              </p>
              <div className="pt-2">
                <Label htmlFor="confirm-delete" className="text-sm">
                  Type <strong>{deleteConfirmModel && getModelDisplayName(deleteConfirmModel)}</strong> to confirm:
                </Label>
                <Input
                  id="confirm-delete"
                  className="mt-2"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Enter model name to confirm"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!deleteConfirmModel || deleteConfirmText !== getModelDisplayName(deleteConfirmModel)}
              onClick={() => deleteConfirmModel && handleDelete(deleteConfirmModel)}
            >
              Delete Model
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
